
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useLanguage } from "@/contexts/LanguageContext";
import { CampaignFormData } from "./types";
import { useEffect } from "react";

interface CompanyInfoFormProps {
  formData: CampaignFormData;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const CompanyInfoForm = ({ formData, onChange }: CompanyInfoFormProps) => {
  const { language, t } = useLanguage();

  // Log form data changes for debugging
  useEffect(() => {
    console.log('CompanyInfoForm: Form data updated:', formData);
  }, [formData]);
  
  // Use the provided onChange handler for instant saving
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    console.log(`CompanyInfoForm: Field ${name} changed to "${value}" - triggering instant save`);
    
    // Call the parent handler which will trigger instant save
    onChange(e);
  };

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border/40 p-4 sm:p-6">
      <h2 className={`text-lg sm:text-xl font-semibold text-foreground mb-4 flex items-center gap-2 ${language === 'ar' ? 'flex-row-reverse justify-start' : 'justify-start'}`}>
        {t("companyInformation")}
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        <div className="space-y-2.5">
          <Label htmlFor="companyName" className="text-foreground font-medium">{t("companyName")} *</Label>
          <Input
            id="companyName"
            name="companyName"
            value={formData.companyName || ''}
            onChange={handleInputChange}
            placeholder={t("enterCompanyName")}
            className={`bg-background/50 dark:bg-card/50 border-border/60 focus:border-primary/50 h-11 ${language === 'ar' ? 'text-right' : 'text-left'}`}
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="brandName">{t("brandName")} *</Label>
          <Input
            id="brandName"
            name="brandName"
            value={formData.brandName || ''}
            onChange={handleInputChange}
            placeholder={t("enterBrandName")}
            className={`bg-background/50 dark:bg-card/50 border-border/60 focus:border-primary/50 h-11 ${language === 'ar' ? 'text-right' : 'text-left'}`}
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="personInCharge">{t("personInCharge")} *</Label>
          <Input
            id="personInCharge"
            name="personInCharge"
            value={formData.personInCharge || ''}
            onChange={handleInputChange}
            placeholder={t("enterPersonName")}
            className={`bg-background/50 dark:bg-card/50 border-border/60 focus:border-primary/50 h-11 ${language === 'ar' ? 'text-right' : 'text-left'}`}
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="jobTitle">{t("jobTitle")} *</Label>
          <Input
            id="jobTitle"
            name="jobTitle"
            value={formData.jobTitle || ''}
            onChange={handleInputChange}
            placeholder={t("enterJobTitle")}
            className={`bg-background/50 dark:bg-card/50 border-border/60 focus:border-primary/50 h-11 ${language === 'ar' ? 'text-right' : 'text-left'}`}
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">{t("campaignEmail")} *</Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email || ''}
            onChange={handleInputChange}
            placeholder={t("enterEmail")}
            className={`bg-background/50 dark:bg-card/50 border-border/60 focus:border-primary/50 h-11 ${language === 'ar' ? 'text-right' : 'text-left'}`}
            dir={language === 'ar' ? 'rtl' : 'ltr'}
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="mobile">{t("mobileNumber")} *</Label>
          <Input
            id="mobile"
            name="mobile"
            type="tel"
            value={formData.mobile || ''}
            onChange={handleInputChange}
            placeholder={t("enterMobile")}
            className={`bg-background/50 dark:bg-card/50 border-border/60 focus:border-primary/50 h-11 ${language === 'ar' ? 'text-right' : 'text-left'}`}
            required
          />
        </div>
      </div>
    </div>
  );
};

export default CompanyInfoForm;
